import { useRef, useState } from 'react';
import type {
  FieldError,
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from 'react-hook-form';
import { useOnClickOutside } from 'usehooks-ts';
import cn from 'classnames';
import { ChevronDownIcon_24, ChevronUpIcon_24, SelectIcon_24 } from 'assets/icons';
import Loader from 'components/Loader';
import Field from '../Field';
import styles from './Picker.module.css';

type PickerProps<TFieldValues extends FieldValues = FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  name: FieldPath<TFieldValues>;
  options: PathValue<TFieldValues, Path<TFieldValues>>[];
  placeholder?: string;
  optionsBottomOffset?: number;
  onOpen?: () => void;
  selectedOption?: PathValue<TFieldValues, Path<TFieldValues>>;
  onSelect?: (option: PathValue<TFieldValues, Path<TFieldValues>>) => void;
  className?: string;
  size: 'large' | 'normal' | 'small';
  editable?: boolean;
  error?: FieldError;
  defaultValue?: string;
  disabled?: boolean;
  disabledOption?: string;
};

const Picker = <TFieldValues extends FieldValues = FieldValues>({
  form,
  name,
  options = [],
  placeholder,
  size,
  selectedOption,
  onSelect,
  className,
  defaultValue,
  disabled = false,
  disabledOption,
}: PickerProps<TFieldValues>): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterValue, setFilterValue] = useState<string | null>(null);
  const filteredOptions = options.filter((option) =>
    filterValue ? option.toLowerCase().includes(filterValue.toLowerCase()) : option,
  );
  const toggleIsOpen = () => {
    if (!disabled) setIsOpen(!isOpen);
    form.clearErrors(name);
  };

  const onFieldFocus = () => {
    setIsOpen(true);
  };

  const ref = useRef(null);
  useOnClickOutside(ref, () => setIsOpen(false));

  const onSelectOption: PickerProps['onSelect'] = (option) => {
    !!onSelect && onSelect(option);
    form.setValue(name, option, { shouldDirty: true });

    setIsOpen(false);
    setFilterValue(null);
  };

  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event.target.value);
  };

  return (
    <div className={cn(styles.dropdownWrapper, className)} ref={ref}>
      <Field
        form={form}
        name={name}
        placeholder={placeholder}
        className={styles.dropdown__header}
        icon={
          isOpen ? (
            options?.length > 0 ? (
              <ChevronUpIcon_24 onClick={toggleIsOpen} />
            ) : (
              <Loader className={cn(styles.loader)} />
            )
          ) : (
            <ChevronDownIcon_24 onClick={toggleIsOpen} />
          )
        }
        onChangeText={onChangeText}
        onFocus={onFieldFocus}
        editable={false}
        defaultValue={defaultValue}
        type={'dropdown'}
        disabled={disabled}
        handleKeyDown={(e) => {
          if (e.key === 'Enter') {
            setIsOpen(false);
          }
        }}
        focusOnChevronClick={isOpen}
        autoComplete={'off'}
      />
      {isOpen && filteredOptions.length > 0 ? (
        <div className={styles['dropdown__listContainer_' + size]}>
          <ul className={styles.dropdown__list}>
            {filteredOptions.map((option) => (
              <li
                className={cn(
                  option === disabledOption
                    ? styles.dropdown__listItem_disabled
                    : styles.dropdown__listItem,
                  styles[size],
                  option === selectedOption && styles.dropdown__listItem_selected,
                )}
                onClick={
                  option !== disabledOption
                    ? onSelectOption.bind(null, option)
                    : undefined
                }
                key={option}
              >
                <>
                  {option} {option === selectedOption && <SelectIcon_24 />}
                </>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default Picker;
